import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The eleventh written page. Everything below comes from the three-part
// full-page capture of digitopia.co/customers/federal-mogul/ sent on
// 2026-08-28 — "tastamam şekilde, aynısını, tıpkısını tasarla" — with the two
// photographs supplied alongside it: the plant from the air, which the
// reference runs in the header, and the assembly line it runs full width
// under the standfirst.
//
// Its shape is Assan Aluminium's, written from the same kind of capture the
// same day, and for the same reason: the reference runs all four parts of the
// story as left-ranged prose on the white page, with no grey panels and no
// lists anywhere. Only the standfirst is centred, and only because the
// template's band is.
//
// The one thing the same request asked for that this page cannot draw is the
// testimonial card at the foot: `content/testimonials.ts` holds twenty quotes
// and none of them is Federal Mogul's. See the check.
export const added = story('federal-mogul', 'Federal Mogul', {
  lede: ['Turning a tradition into a thriving engine.'],
  heroPhoto: {
    src: '/photo/federal-mogul-plant-aerial.webp',
    alt: 'Federal Mogul’s plant from the air: long orange-and-grey production halls and a '
      + 'flat-roofed warehouse between the lake shore and the railway line, the town at its '
      + 'edge.',
  },
  intro: [
    'Federal-Mogul Corporation is an American developer, manufacturer, and supplier of '
    + 'products for automotive, commercial, aerospace, marine, rail and off-road vehicles; '
    + 'and industrial, agricultural, and power-generation applications. It was acquired in '
    + 'October 2018 by Tenneco.',
    'Federal-Mogul operated two independent business divisions, Federal-Mogul Powertrain, '
    + 'and Federal-Mogul Motorparts. The two divisions each had their own chief executive '
    + 'officer.',
  ],
  photo: {
    src: '/photo/federal-mogul-assembly-line.webp',
    alt: 'A conveyor running the length of a Federal Mogul production hall, past blue and '
      + 'orange machining cells and safety cages, on a green-painted floor.',
  },
  sections: [
    {
      heading: 'The Need?',
      body: [
        'The automotive industry is in huge turmoil. Everything is shaking up. New players, '
        + 'new form factors, new materials, new propulsion, new fuels, basically new '
        + 'everything. Everybody must adopt or will be disrupted.',
        'Federal Mogul wants to adopt as well. The fourth industrial revolution is upon them. '
        + 'Integrating processes and entire value chains is good for business and even good '
        + 'for the planet. Collecting data for the sake of analysis, optimization, and '
        + 'forecasting is needed to better understand what’s going on in and around the '
        + 'business.',
        'Therefore, Federal Mogul has started a digital transformation program. They needed '
        + 'consultants to help them define the path, align executives, measure and benchmark '
        + 'where they are, determine the targets, and facilitate to agree on a shared vision.',
      ],
    },
    {
      heading: 'The Solution',
      body: [
        'Digitopia was hired to guide Federal Mogul’s executive team. Several workshops were '
        + 'conducted to get mutual understanding of the current and target state. Digitopia '
        + 'provided the Digital Maturity Index service, so Federal Mogul realized their '
        + 'digital maturity standings. Now they know.',
        'The digital maturity scorecard provided in-depth understanding of the digital '
        + 'capabilities in six dimensions of the business. This showed the strengths and '
        + 'weaknesses. Based on this analysis priorities were identified, and investment '
        + 'decisions were taken.',
      ],
    },
    {
      heading: 'The Benefits',
      body: [
        'You manage what you measure. Federal Mogul executive leadership team agreed on a '
        + 'shared vision. This agreement helped them to determine the objectives of their '
        + 'digital transformation program. This enabled the management to prioritize their '
        + 'investments. Eventually all these activities will assure their progress and '
        + 'accelerate their journey. This will yield more benefits and faster return cycles. '
        + 'Digital transformation is a means to an end, and the ultimate goal is making '
        + 'Federal Mogul more successful, more productive, more customer centric, more agile, '
        + 'and digitally more future proof.',
      ],
    },
    {
      heading: 'The Next Steps',
      body: [
        'Federal Mogul is continuing their successful digital transformation journey. '
        + 'Investments are carried out, projects are realized, new capabilities are '
        + 'developed. Looking at the overall situation regularly is highly appreciated. '
        + 'Trusted partners such as Digitopia are always welcomed. We wish Federal Mogul best '
        + 'of luck on their digital journey.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a three-part full-page capture of '
    + `${STORY_SOURCE}/federal-mogul/ supplied on 2026-08-28, not from a file we hold, so `
    + 'please proofread it against the source before production. Two things in it are the '
    + 'reference’s own and were left verbatim rather than silently corrected: it writes '
    + '“Everybody must adopt or will be disrupted” and “Federal Mogul wants to adopt as well” '
    + 'where the sense is *adapt*, and it spells the company “Federal-Mogul” with a hyphen in '
    + 'the opening band and “Federal Mogul” without one everywhere else — the card, the '
    + 'index and this page’s headline all follow the unhyphenated form the old site’s own '
    + 'link uses. **The testimonial card asked for at the foot of this page is not drawn, and '
    + 'cannot be until somebody supplies the quote.** Federal Mogul’s mark is in hand (it is '
    + 'the plate on the Customers grid, and would head the card) and the template puts a '
    + 'portrait of the speaker beside it, but content/testimonials.ts holds twenty quotes and '
    + 'none of them is Federal Mogul’s — the reference page has no quote on it either, so '
    + 'there was nothing to copy. Send the words, the speaker’s name and title and their '
    + 'photograph, and the card appears in one entry there; writing a plausible-sounding '
    + 'quote for a named customer was the one thing not on the table. Two deliberate '
    + 'differences from the reference, each of them a template-wide decision rather than this '
    + 'page’s: the header eyebrow reads “Digitopia / Customer Success Story” where '
    + 'digitopia.co reads “SUCCESS STORIES”, and this story has no PDF, so there is no “Read '
    + 'the full story” button — it was not among the sixteen documents sent on 2026-08-27. '
    + 'One production note: both photographs are small originals — 768×432 and 1200×455, '
    + 'which is what the live site serves — so they are drawn at that width and will look '
    + 'soft on a high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 18
